import dayjs, { type Dayjs } from "dayjs";

import {
  type ExerciseInWorkout,
  type ProgramWorkout,
  type SuperSet,
} from "@fitness-app/data-models/entities/TrainingProgram";

const DATE_FORMAT = "DD.MM.YYYY";

export const formatChallengeDate = (date?: string | null): string => (date ? dayjs(date).format(DATE_FORMAT) : "-");

export const formatChallengeDateRange = (startDate?: string | null, endDate?: string | null): string =>
  `${formatChallengeDate(startDate)} – ${formatChallengeDate(endDate)}`;

export const formatChallengeDateTime = (date?: string | null): string =>
  date ? dayjs(date).format(`${DATE_FORMAT} HH:mm`) : "-";

/** TRE-1654 — safety net for the weekly event generator: a mis-set range can't write thousands of rows. */
export const MAX_GENERATED_EVENT_OCCURRENCES = 200;

/**
 * Every occurrence of a weekly rule inside [first, until], keeping the time-of-day of `first`.
 * dayjs `day()` numbering (0 = Sunday); an empty `daysOfWeek` falls back to the first occurrence's
 * weekday, which is what "co sobotę" means when the trainer only picked a Saturday start.
 */
export const generateWeeklyOccurrences = (first: Dayjs, until: Dayjs, daysOfWeek: number[]): Dayjs[] => {
  const days = daysOfWeek.length ? daysOfWeek : [first.day()];
  const lastDay = until.endOf("day");
  const occurrences: Dayjs[] = [];

  for (let cursor = first; !cursor.isAfter(lastDay); cursor = cursor.add(1, "day")) {
    if (days.includes(cursor.day())) {
      occurrences.push(cursor);
      if (occurrences.length >= MAX_GENERATED_EVENT_OCCURRENCES) {
        break;
      }
    }
  }

  return occurrences;
};

/**
 * All exercises of a workout flagged `requireExerciseVideoRecording` (superset members included).
 * An `exerciseRecording` challenge task scores recordings of exactly one flagged exercise —
 * its id becomes the task's `parentExerciseId`.
 */
export const getFlaggedRecordingExercises = (workout: ProgramWorkout): ExerciseInWorkout[] =>
  [...workout.preWorkout, ...workout.exercises, ...workout.postWorkout]
    .flatMap((item) =>
      (item as unknown as SuperSet).isSuperSet ? (item as unknown as SuperSet).mergedExercises : [item],
    )
    .filter((exercise) => exercise.requireExerciseVideoRecording);
